import React, { useState } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  holdings: Holding[];
}

export const DividendRetirementPlanner: React.FC<Props> = ({ holdings }) => {
  const { language } = useLanguage();
  const [monthlyGoal, setMonthlyGoal] = useState<number>(2000);
  const [targetYears, setTargetYears] = useState<number>(10);
  const [monthlyContribution, setMonthlyContribution] = useState<number>(500);

  const totalValue = holdings.reduce((sum, h) => sum + (h.quantity * (h.current_price || h.avg_cost)), 0);
  const avgYieldPct = 3.8; // Estimated portfolio dividend yield %
  const currentAnnualDiv = totalValue * (avgYieldPct / 100);
  const currentMonthlyDiv = currentAnnualDiv / 12;

  // Required capital for target monthly dividend
  const targetAnnualDiv = monthlyGoal * 12;
  const requiredCapital = targetAnnualDiv / (avgYieldPct / 100);
  const capitalGap = Math.max(0, requiredCapital - totalValue);

  // Projection with compounding (7% market growth + reinvested dividend)
  const projectedYears = Array.from({ length: Math.min(targetYears, 30) }, (_, i) => {
    const yr = i + 1;
    const futureVal = totalValue * Math.pow(1 + 0.07, yr) + (monthlyContribution * 12 * ((Math.pow(1 + 0.07, yr) - 1) / 0.07));
    const futureMonthlyDiv = (futureVal * (avgYieldPct / 100)) / 12;
    return { yr, futureVal, futureMonthlyDiv };
  });

  return (
    <div className="glass-panel p-6 space-y-6 font-mono">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 pb-4 border-b border-[var(--border-color)]">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[var(--text-primary)] flex items-center gap-2">
            <span>💸</span>
            {language === 'sk' ? 'Plánovač Pasívneho Príjmu & Dôchodku' : 'Dividend & Retirement Planner'}
          </h3>
          <p className="text-[10px] text-stone-500 mt-0.5">
            {language === 'sk' ? 'Kalkulačka mesačnej výplaty dividend a finančnej nezávislosti' : 'Passive income calculator and financial independence roadmap'}
          </p>
        </div>

        <div className="text-right">
          <span className="text-[9px] uppercase font-bold text-stone-500 block">
            {language === 'sk' ? 'Aktuálny Pasívny Príjem' : 'Current Monthly Income'}
          </span>
          <span className="text-xl font-black text-emerald-600">
            €{currentMonthlyDiv.toFixed(2)} / {language === 'sk' ? 'mes' : 'mo'}
          </span>
        </div>
      </div>

      {/* Input controls */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-stone-50 p-4 rounded-2xl border border-stone-200/80 text-xs">
        <div>
          <label className="block text-[9px] uppercase font-extrabold text-stone-500 mb-1">
            {language === 'sk' ? 'Cieľový Mesačný Príjem (€)' : 'Target Monthly Income (€)'}
          </label>
          <input
            type="number"
            value={monthlyGoal}
            onChange={(e) => setMonthlyGoal(Number(e.target.value))}
            className="w-full bg-white border border-stone-300 rounded-xl p-2 font-bold outline-none text-stone-900"
          />
        </div>

        <div>
          <label className="block text-[9px] uppercase font-extrabold text-stone-500 mb-1">
            {language === 'sk' ? 'Mesačný Vklad (€)' : 'Monthly Contribution (€)'}
          </label>
          <input
            type="number"
            value={monthlyContribution}
            onChange={(e) => setMonthlyContribution(Number(e.target.value))}
            className="w-full bg-white border border-stone-300 rounded-xl p-2 font-bold outline-none text-stone-900"
          />
        </div>

        <div>
          <label className="block text-[9px] uppercase font-extrabold text-stone-500 mb-1">
            {language === 'sk' ? 'Horizont (Roky)' : 'Time Horizon (Years)'}
          </label>
          <input
            type="number"
            value={targetYears}
            onChange={(e) => setTargetYears(Number(e.target.value))}
            className="w-full bg-white border border-stone-300 rounded-xl p-2 font-bold outline-none text-stone-900"
          />
        </div>
      </div>

      {/* Metrics Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Potrebný Kapitál' : 'Required Capital'}
          </span>
          <span className="text-base font-black text-stone-900">
            €{requiredCapital.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>

        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Chýbajúci Kapitál' : 'Capital Gap'}
          </span>
          <span className="text-base font-black text-amber-700">
            €{capitalGap.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </span>
        </div>

        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Odhad. Výnos Sektora' : 'Est. Dividend Yield'}
          </span>
          <span className="text-base font-black text-indigo-700">
            {avgYieldPct}% p.a.
          </span>
        </div>

        <div className="stat-card-premium">
          <span className="text-[9px] uppercase font-bold text-stone-500 block mb-1">
            {language === 'sk' ? 'Pokrytie Cieľa' : 'Target Coverage'}
          </span>
          <span className="text-base font-black text-emerald-700">
            {((currentMonthlyDiv / (monthlyGoal || 1)) * 100).toFixed(1)}%
          </span>
        </div>
      </div>

      {/* Trajectory Table */}
      <div className="overflow-x-auto border border-stone-200 rounded-2xl">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-stone-50 border-b border-stone-200 text-stone-600 font-bold uppercase text-[9px]">
              <th className="p-2.5">{language === 'sk' ? 'Rok' : 'Year'}</th>
              <th className="p-2.5 text-right">{language === 'sk' ? 'Odhad. Hodnota Portfólia' : 'Est. Portfolio Value'}</th>
              <th className="p-2.5 text-right">{language === 'sk' ? 'Mesačný Pasívny Príjem' : 'Est. Monthly Income'}</th>
            </tr>
          </thead>
          <tbody>
            {projectedYears.slice(0, 5).map((p) => (
              <tr key={p.yr} className="border-b border-stone-100 hover:bg-stone-50">
                <td className="p-2.5 font-bold text-stone-900">Rok {p.yr}</td>
                <td className="p-2.5 text-right font-extrabold text-stone-900">€{p.futureVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                <td className="p-2.5 text-right font-black text-emerald-600">€{p.futureMonthlyDiv.toFixed(2)} / mes</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
